import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './index.scss';

const GAP = 4;

const getTooltipCoords = (rect, tooltipEl, placement) => {
  const tooltipWidth = tooltipEl?.offsetWidth || 0;
  const tooltipHeight = tooltipEl?.offsetHeight || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top;
  let left;

  switch (placement) {
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + GAP;
      break;
    case 'top':
      top = rect.top - tooltipHeight - GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
    default:
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - GAP;
      break;
  }

  // Clamp to viewport
  if (top + tooltipHeight > viewportHeight - GAP) {
    top = viewportHeight - tooltipHeight - GAP;
  }
  if (top < GAP) {
    top = GAP;
  }
  if (left + tooltipWidth > viewportWidth - GAP) {
    left = viewportWidth - tooltipWidth - GAP;
  }
  if (left < GAP) {
    left = GAP;
  }

  return { top, left };
};

const Tooltip = ({ id, trigger, content, position: placement }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords(getTooltipCoords(rect, tooltipRef.current, placement));
  }, [placement]);

  useEffect(() => {
    if (visible) {
      updatePosition();
    }
  }, [visible, updatePosition]);

  const showTooltip = useCallback(() => {
    setVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        aria-describedby={visible ? id : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex="0"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        style={{ display: 'inline-block' }}
      >
        {trigger}
      </div>
      {visible &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            id={id}
            role="tooltip"
            className="aria-tooltip"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

Tooltip.defaultProps = {
  position: 'left',
};

Tooltip.propTypes = {
  id: PropTypes.string.isRequired,
  trigger: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
};

export default Tooltip;
